import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { z } from 'zod';

import type { ListCoordinators } from '@virtual-care/client';
import { Button, DialogFooter, Input, Label } from '@virtual-care/shadcn';
import {
  FieldMessage,
  FormMessage,
  client,
  errorToFormIssue,
} from '@virtual-care/ui';

import { CreateDialog } from '../../components/create-dialog.tsx';

const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email format' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
});

export function UpdateCoordinatorDialog({
  coordinator,
  trigger,
  onSave,
}: {
  coordinator: ListCoordinators['records'][number];
  trigger: React.ReactNode;
  onSave?: () => void;
}) {
  const [open, setOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      email: coordinator.email,
      firstName: coordinator.firstName,
      lastName: coordinator.lastName,
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await client.request('PATCH /coordinators/{id}', {
            id: coordinator.id,
            ...value,
          });
          setOpen(false);
          onSave?.();
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={open}
      onOpenChange={setOpen}
      title="Edit Coordinator"
      subtitle="Update coordinator information"
      onSave={() => onSave?.()}
      trigger={trigger}
    >
      <form.Field
        name="email"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="coordinator@hospital.com"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="firstName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="أحمد"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="lastName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="السعد"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}
